// ============================================
// PriceAlertUI.jsx
// Two pieces of UI driven by the usePriceAlerts hook:
//
// PriceAlertMenu — pops up at long-press position. Shows the cursor mcap,
//   an above/below toggle, and a list of existing alerts on the current
//   chart with delete affordances. Closes on outside click or Esc.
//
// PriceAlertNotifications — fixed-position toast stack (bottom-right).
//   Each toast click switches to that token (via onSwitchToken from the
//   hook) and dismisses. X dismisses without switching.
// ============================================
import { useEffect, useRef } from 'react'

const MENU_W = 240
const MENU_PAD = 8

export function PriceAlertMenu({
  menuState,             // { x, y, mcap, mint } from hook
  alertsForCurrentToken,
  onAdd,                 // (mint, mcap, direction) => void
  onRemove,              // (id) => void
  onClose,
  formatMcap,
  currentMcap,           // for showing direction inference hint
}) {
  const ref = useRef(null)

  // Outside-click + Esc to close
  useEffect(() => {
    if (!menuState) return
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose()
    }
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    // Defer so the same pointerup that opened us doesn't immediately
    // register as outside.
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDown)
      document.addEventListener('keydown', onKey)
    }, 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuState, onClose])

  if (!menuState) return null

  // Clamp menu to viewport
  const x = Math.min(window.innerWidth  - MENU_W - MENU_PAD, Math.max(MENU_PAD, menuState.x))
  const yEstimate = 220 + Math.min(120, alertsForCurrentToken.length * 28)
  const y = Math.min(window.innerHeight - yEstimate - MENU_PAD, Math.max(MENU_PAD, menuState.y))

  // Default direction inferred from cursor vs current price
  const defaultDir = (Number.isFinite(currentMcap) && menuState.mcap >= currentMcap) ? 'above' : 'below'

  const fire = (direction) => {
    onAdd({
      market: menuState.market || 'sol',
      mint:   menuState.mint   || null,
      symbol: menuState.symbol || null,
      mcap:   menuState.mcap,
      direction,
    })
    onClose()
  }

  return (
    <div
      ref={ref}
      className="price-alert-menu"
      style={{
        position: 'fixed', left: `${x}px`, top: `${y}px`, width: `${MENU_W}px`,
        background: '#16181d', border: '1px solid #2a2d35', borderRadius: 6,
        boxShadow: '0 10px 30px rgba(0,0,0,0.6)', zIndex: 9000,
        fontSize: 12, color: '#e8eaed', padding: 10,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          New Alert
        </span>
        <span
          onClick={onClose}
          style={{ cursor: 'pointer', color: '#666', fontSize: 14, lineHeight: 1, padding: '0 4px' }}
        >×</span>
      </div>

      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#ffd700' }}>
          {formatMcap(menuState.mcap)}
        </div>
        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>
          when price crosses {defaultDir === 'above' ? '↑' : '↓'} this level
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button
          onClick={() => fire('above')}
          style={{
            flex: 1, padding: '7px 0', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: defaultDir === 'above' ? '#1f4f2a' : '#1a1c20',
            color: '#22c55e', border: '1px solid #2a4a32', cursor: 'pointer',
          }}
        >↑ Cross Above</button>
        <button
          onClick={() => fire('below')}
          style={{
            flex: 1, padding: '7px 0', borderRadius: 4, fontSize: 11, fontWeight: 600,
            background: defaultDir === 'below' ? '#4f1f1f' : '#1a1c20',
            color: '#ef4444', border: '1px solid #4a2a2a', cursor: 'pointer',
          }}
        >↓ Cross Below</button>
      </div>

      {alertsForCurrentToken.length > 0 && (
        <>
          <div style={{
            fontSize: 10, color: '#6b7280', textTransform: 'uppercase',
            letterSpacing: 0.5, paddingTop: 8, borderTop: '1px solid #25272d', marginBottom: 4,
          }}>
            Active on this chart
          </div>
          <div style={{ maxHeight: 140, overflowY: 'auto' }}>
            {alertsForCurrentToken.map(a => (
              <div key={a.id} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '4px 0', fontSize: 11,
              }}>
                <span style={{ color: a.direction === 'above' ? '#22c55e' : '#ef4444' }}>
                  {a.direction === 'above' ? '↑' : '↓'} {formatMcap(a.mcap)}
                </span>
                <span
                  onClick={() => onRemove(a.id)}
                  style={{ cursor: 'pointer', color: '#666', padding: '0 6px' }}
                  title="Remove"
                >×</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Shared toast card: the price-alert layout, reused by the followed-wallet
// alerts (WalletAlertNotifications). accent: 'green' | 'red' | 'amber'.
const ACCENTS = {
  green: { border: '#2a4a32', bar: '#22c55e' },
  red:   { border: '#4a2a2a', bar: '#ef4444' },
  amber: { border: '#4a3f22', bar: '#f59e0b' },
}
export function AlertToastCard({ title, line, sub, accent = 'green', onClick, onDismiss, action }) {
  const a = ACCENTS[accent] || ACCENTS.green
  return (
    <div
      onClick={onClick}
      style={{
        pointerEvents: 'auto',
        background: '#1a1c22',
        border: `1px solid ${a.border}`,
        borderLeft: `4px solid ${a.bar}`,
        borderRadius: 6, padding: '10px 12px',
        minWidth: 240, maxWidth: 320,
        cursor: 'pointer', color: '#e8eaed',
        boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
        display: 'flex', flexDirection: 'column', gap: 4,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <span style={{ fontSize: 11, color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </span>
        <span
          onClick={(e) => { e.stopPropagation(); onDismiss && onDismiss() }}
          className="alert-toast-x" title="Dismiss"
        ><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"><path d="M5 5L19 19M19 5L5 19" /></svg></span>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, overflowWrap: 'anywhere' }}>
        {line}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 10, color: '#6b7280' }}>{sub}</span>
        {action && (
          <button
            onClick={(e) => { e.stopPropagation(); action.onClick() }}
            style={{
              fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 4, cursor: 'pointer',
              background: '#1f2937', color: '#e8eaed', border: '1px solid #374151',
            }}
          >{action.label}</button>
        )}
      </div>
    </div>
  )
}

export function PriceAlertNotifications({
  notifications,
  onClick,
  onDismiss,
  formatMcap,
}) {
  if (notifications.length === 0) return null

  return (
    <div style={{
      position: 'fixed', bottom: 16, right: 16, zIndex: 9100,
      display: 'flex', flexDirection: 'column-reverse', gap: 8, pointerEvents: 'none',
    }}>
      {notifications.map(n => (
        <AlertToastCard
          key={n.id}
          title={n.title || 'Alert Triggered'}
          accent={n.accent || (n.direction === 'above' ? 'green' : 'red')}
          line={n.line || `${n.symbol} ${n.direction === 'above' ? '↑' : '↓'} ${formatMcap(n.mcap)}`}
          sub={n.sub || 'click to view chart'}
          onClick={() => onClick(n)}
          onDismiss={() => onDismiss(n.id)}
        />
      ))}
    </div>
  )
}

// Followed-wallet alerts (follow-alerts-20261018). Same card; the stack sits
// just left of the price-alert stack on wide screens so the two never cover
// each other, and above it on narrow ones.
export function WalletAlertNotifications({ notifications, onClick, onDismiss, onFollow }) {
  if (!notifications || notifications.length === 0) return null
  const wide = typeof window !== 'undefined' && window.innerWidth >= 760
  return (
    <div style={{
      position: 'fixed', bottom: wide ? 16 : 120, right: wide ? 352 : 16, zIndex: 9100,
      display: 'flex', flexDirection: 'column-reverse', gap: 8, pointerEvents: 'none',
    }}>
      {notifications.map(n => (
        <AlertToastCard
          key={n.id}
          title={n.title}
          accent={n.accent}
          line={n.line}
          sub={n.sub}
          onClick={() => onClick(n)}
          onDismiss={() => onDismiss(n.id)}
          action={n.kind === 'xfer' && n.canFollow && onFollow
            ? { label: '+ Follow', onClick: () => { onFollow(n.to); onDismiss(n.id) } }
            : null}
        />
      ))}
    </div>
  )
}
